import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { shipment } from '../../../../models/entities/shipment/shipment';
import { PagesRoutes } from '../../../../routing/PagesRoutes';
import { filterFieldsType } from '../../../../state/ducks/allShipmentsPage/allShipmentsPage';
import { businessPartnersSelectors } from '../../../../state/ducks/businessPartner/selectors';
import { businessPartner } from '../../../../models/entities/businessPartner/businessPartner';
import { localizationSelectors } from '../../../../state/ducks/localization/selectors';
import { shipmentsSelectors } from '../../../../state/ducks/shipments/selectors';
import { RootState } from '../../../../state/store/store';
import { localSDK as client } from '../../../../sdk';
import { consolidationMeta } from '../../../../sdk/controllers/consolidations/consolidations';
import Grid, { gridSortType, orderGridDataFn } from '../../../shared/Grid/Grid';
import { filterColsByUserSettings } from '../../../shared/Grid/util';
import { userSelectors } from '../../../../state/ducks/user/selectors';
import ConsolidationsExportMenu from './ConsolidationsExportMenu';
import { ColumnType } from '../../../shared/Grid/types/Column';
import StyledLink from '../../../shared/SmallComponents/StyledLink';
import { styled } from '../../../shared/Theme/theme';
import { RightArrowIcon } from '../../../shared/Grid/CellRenderer/CellRenderer';
import ShipmentStation from '../../../shared/Grid/specialColumns/ShipmentStation';
import ConsolidationsPageHeader from './ConsolidationsPageHeader';
import ConsolidationOperationalStatusCell from './ConsolidationOperationalStatusCell';
import { applyConsolidationShipmentFilters } from './filter/consolidationsFilterUtils';
import { defaultConsolidationFilters, normalizeConsolidationFilters } from './filter/consolidationsFilterTypes';
import { isAirOrOceanConsolidationGroup } from './consolidationUtils';

type ConsolidationRow = {
    id: string;
    master: string;
    handlingAgent: string;
    shipmentCount: number;
    route: string;
    originStation?: string;
    destinationStation?: string;
    originCountry?: string;
    destinationCountry?: string;
    flightOrVoyage: string;
    etd?: Date;
    eta?: Date;
    totalWeight: number;
    totalVolume: number;
    status: string;
    isActive: boolean;
    operationalStatus: 'OPEN' | 'CLOSED';
};

type Props = {
    shipments: shipment[];
    localization: any;
    showLoader?: boolean;
    fetchShipments: () => void;
    getBusinessPartnerById: (id: string) => businessPartner | undefined;
    consolidationGridSettings?: ReturnType<typeof userSelectors.consolidationGridSettings>;
};

const DEFAULT_GRID_SORT: gridSortType<ConsolidationRow> = { master: 'asc' };
const USER_SETTING_KEY = 'ConsolidationGrid';

const ROWS_PER_PAGE = 30;

const numberOrZero = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const resolveHandlingAgentName = (
    groupedShipments: shipment[],
    getBusinessPartnerById: (id: string) => businessPartner | undefined
) => {
    const agentId = groupedShipments.map((s) => s.handlingAgent).find((id) => !!id && String(id).trim());
    if (!agentId) return '-';
    return getBusinessPartnerById(agentId)?.name || agentId;
};

const ConsolidationsPageConnected = ({
    shipments,
    localization,
    showLoader,
    fetchShipments,
    getBusinessPartnerById,
    consolidationGridSettings
}: Props) => {
    const [textFilter, setTextFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [filterFields, setFilterFieldsState] = useState<filterFieldsType>(defaultConsolidationFilters);
    const [showInactive, setShowInactive] = useState(false);
    const [metadataByMaster, setMetadataByMaster] = useState<Record<string, consolidationMeta>>({});
    const [gridSort, setGridSort] = useState<gridSortType<ConsolidationRow>>(DEFAULT_GRID_SORT);

    const setFilterFields = useCallback((filter: filterFieldsType) => {
        setFilterFieldsState(normalizeConsolidationFilters(filter));
        setCurrentPage(0);
    }, []);

    useEffect(() => {
        fetchShipments();
    }, [fetchShipments]);

    const baseConsolidations = useMemo<ConsolidationRow[]>(() => {
        const filteredShipments = applyConsolidationShipmentFilters(shipments, filterFields);

        const groups = filteredShipments.reduce<Record<string, shipment[]>>((acc, currentShipment) => {
            if (!currentShipment.master) return acc;
            if (!acc[currentShipment.master]) acc[currentShipment.master] = [];
            acc[currentShipment.master].push(currentShipment);
            return acc;
        }, {});

        return Object.entries(groups)
            .filter(([_, groupedShipments]) => isAirOrOceanConsolidationGroup(groupedShipments))
            .map(([master, groupedShipments]) => {
                const firstShipment = groupedShipments[0];
                const meta = metadataByMaster[master];
                const totalWeight = groupedShipments.reduce((sum, item) => sum + numberOrZero(item.weight), 0);
                const totalVolume = groupedShipments.reduce((sum, item) => sum + numberOrZero(item.volume), 0);

                return {
                    id: master,
                    master,
                    handlingAgent: resolveHandlingAgentName(groupedShipments, getBusinessPartnerById),
                    shipmentCount: groupedShipments.length,
                    route: `${firstShipment.originStation || '-'} → ${firstShipment.destinationStation || '-'}`,
                    originStation: firstShipment.originStation,
                    destinationStation: firstShipment.destinationStation,
                    originCountry: firstShipment.originCountry,
                    destinationCountry: firstShipment.destinationCountry,
                    flightOrVoyage: firstShipment.voyageNumber || firstShipment.vesselName || '-',
                    etd: firstShipment.etd,
                    eta: firstShipment.eta,
                    totalWeight,
                    totalVolume,
                    status: firstShipment.state || '-',
                    isActive: meta?.isActive ?? true,
                    operationalStatus: meta?.operationalStatus ?? 'OPEN'
                };
            });
    }, [shipments, filterFields, getBusinessPartnerById, metadataByMaster]);

    useEffect(() => {
        const masters = baseConsolidations.map((c) => c.master);
        if (!masters.length) {
            setMetadataByMaster({});
            return;
        }
        void client
            .consolidations()
            .fetchMetadata(masters)
            .then((rows) => {
                const next: Record<string, consolidationMeta> = {};
                rows.forEach((r) => {
                    next[r.master] = r;
                });
                setMetadataByMaster(next);
            })
            .catch(() => {
                /* keep defaults when API unavailable */
            });
    }, [baseConsolidations.map((c) => c.master).join('|')]);

    const handleOperationalUpdated = useCallback((master: string, status: 'OPEN' | 'CLOSED') => {
        setMetadataByMaster((prev) => ({
            ...prev,
            [master]: {
                master,
                operationalStatus: status,
                isActive: status === 'CLOSED' ? false : prev[master]?.isActive ?? true
            }
        }));
    }, []);

    const filteredConsolidations = useMemo(() => {
        let rows = baseConsolidations;
        if (!showInactive) {
            rows = rows.filter((r) => r.isActive);
        }
        const query = textFilter.trim().toLowerCase();
        if (!query) return rows;

        return rows.filter((row) => {
            return (
                row.master.toLowerCase().includes(query) ||
                row.handlingAgent.toLowerCase().includes(query) ||
                row.route.toLowerCase().includes(query) ||
                row.flightOrVoyage.toLowerCase().includes(query) ||
                row.status.toLowerCase().includes(query)
            );
        });
    }, [baseConsolidations, textFilter, showInactive]);

    const columns: Array<ColumnType<ConsolidationRow>> = useMemo(
        () => [
            {
                field: 'master',
                title: 'Master AWB',
                width: '200px',
                sortable: true,
                renderer: (cellValue) => (
                    <StyledLink to={`${PagesRoutes.ShipmentsConsolidations}/${encodeURIComponent(cellValue)}`}>
                        {cellValue}
                    </StyledLink>
                )
            },
            { field: 'handlingAgent', title: 'Handling Agent', width: '160px', sortable: true },
            { field: 'shipmentCount', title: 'Shipments', width: '100px', sortable: true, columnType: 'numberWithCommas' },
            {
                field: 'route',
                title: 'Route',
                width: '200px',
                sortable: true,
                bindedFields: ['originCountry', 'originStation', 'destinationCountry', 'destinationStation'],
                renderer: (_, binded) => {
                    const originCountry = typeof binded?.[0] === 'string' ? binded[0] : '';
                    const originStation = typeof binded?.[1] === 'string' ? binded[1] : '-';
                    const destinationCountry = typeof binded?.[2] === 'string' ? binded[2] : '';
                    const destinationStation = typeof binded?.[3] === 'string' ? binded[3] : '-';
                    return (
                        <RouteCell>
                            {originCountry ? (
                                <ShipmentStation country={originCountry} port={originStation} />
                            ) : (
                                <span>{originStation}</span>
                            )}
                            <RouteArrow aria-hidden />
                            {destinationCountry ? (
                                <ShipmentStation country={destinationCountry} port={destinationStation} />
                            ) : (
                                <span>{destinationStation}</span>
                            )}
                        </RouteCell>
                    );
                }
            },
            { field: 'flightOrVoyage', title: 'Flight / Voyage', width: '140px', sortable: true },
            { field: 'etd', title: 'ETD', width: '120px', sortable: true, columnType: 'date' },
            { field: 'eta', title: 'ETA', width: '120px', sortable: true, columnType: 'date' },
            { field: 'totalWeight', title: 'Total Weight', width: '120px', sortable: true, columnType: 'numberWithCommas' },
            { field: 'totalVolume', title: 'Total Volume', width: '120px', sortable: true, columnType: 'numberWithCommas' },
            {
                field: 'operationalStatus',
                title: 'Operational',
                width: '130px',
                sortable: true,
                bindedFields: ['master', 'operationalStatus'],
                renderer: (_, binded) => (
                    <ConsolidationOperationalStatusCell
                        master={String(binded?.[0] || '')}
                        operationalStatus={(binded?.[1] as 'OPEN' | 'CLOSED') || 'OPEN'}
                        onUpdated={(status) => handleOperationalUpdated(String(binded?.[0] || ''), status)}
                    />
                )
            },
            { field: 'status', title: 'Status', width: '120px', minWidth: '120px', sortable: true }
        ],
        [handleOperationalUpdated]
    );

    const displayColumns = useMemo(
        () => filterColsByUserSettings(columns, consolidationGridSettings),
        [columns, consolidationGridSettings]
    );

    const sortedConsolidations = useMemo(
        () => orderGridDataFn(filteredConsolidations, gridSort, displayColumns),
        [filteredConsolidations, gridSort, displayColumns]
    );

    const paginatedConsolidations = useMemo(() => {
        const start = currentPage * ROWS_PER_PAGE;
        return sortedConsolidations.slice(start, start + ROWS_PER_PAGE);
    }, [sortedConsolidations, currentPage]);

    const onGridSortChange = useCallback((sort: gridSortType<ConsolidationRow>) => {
        setGridSort(sort);
        setCurrentPage(0);
    }, []);

    return (
        <PageWrapper>
            <ConsolidationsPageHeader
                totalCount={filteredConsolidations.length}
                isLoading={showLoader}
                filterFields={filterFields}
                showInactive={showInactive}
                exportSlot={
                    <ConsolidationsExportMenu
                        rows={sortedConsolidations}
                        columns={displayColumns}
                        localization={localization}
                    />
                }
                onToggleActiveView={() => {
                    setShowInactive((v) => !v);
                    setCurrentPage(0);
                }}
                onRefresh={fetchShipments}
                onFilterChange={setTextFilter}
                onApplyFilters={(filter) => setFilterFields(filter)}
                currentPage={currentPage}
                rowsPerPage={ROWS_PER_PAGE}
                onPageChange={setCurrentPage}
            />
            <GridWrapper>
                <Grid<ConsolidationRow>
                    columns={displayColumns}
                    data={paginatedConsolidations}
                    localization={localization.grid}
                    emptyStateText={showInactive ? 'No consolidations found.' : 'No active consolidations found.'}
                    showLoader={showLoader}
                    idKey="id"
                    firstColumnPaddingLeft="52px"
                    gridSort={gridSort}
                    setGridSort={onGridSortChange}
                    onSettingsClick={() => undefined}
                    onRowMoreOptionsClick={() => undefined}
                    userSettingKey={USER_SETTING_KEY}
                />
            </GridWrapper>
        </PageWrapper>
    );
};

const PageWrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const GridWrapper = styled.div`
    flex: 1;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
    overflow: overlay;
    height: 100%;
    z-index: 1;

    @media screen and (min-width: 768px) {
        height: calc(100% - 127px);
    }
`;

const RouteCell = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
`;

const RouteArrow = styled(RightArrowIcon)`
    flex-shrink: 0;
    margin: 0 4px 0 0;
`;

const mapStateToProps = (state: RootState) => ({
    shipments: shipmentsSelectors.shipmentsData(state),
    localization: localizationSelectors.allShipmentsPage(state),
    showLoader: state.loading.effects.shipments.fetchShipments,
    getBusinessPartnerById: (id: string) => businessPartnersSelectors.getById(state, id),
    consolidationGridSettings: userSelectors.consolidationGridSettings(state)
});

const mapDispatchToProps = (dispatch: any) => ({
    fetchShipments: () => dispatch.shipments.fetchShipments()
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsolidationsPageConnected);
